/**
 * La déclaration de non-condamnation et de filiation, du formulaire au texte.
 *
 * Le document n'est pas un Cerfa : c'est une lettre libre, dont l'administration
 * publie un modèle (Service-Public, « Modèle de lettre - Déclaration de
 * non-condamnation et de filiation », vérifié le 1er avril 2026, licence
 * etalab-2.0). Ce module compose ce modèle mot pour mot à partir des réponses,
 * et rien d'autre : ni mise en page, ni PDF, ni React. C'est ce qui permet au
 * harnais de le juger sans navigateur.
 *
 * TROIS ÉCARTS ASSUMÉS AVEC LE MODÈLE DE LA DILA, et aucun autre :
 *   1. Elle écrit « code du commerce » ; le code s'appelle « code de commerce »
 *      sur Légifrance, et c'est le nom que nous portons.
 *   2. Elle écrit « Je soussigné(e) » et « né(e) » ; la maison n'écrit pas en
 *      inclusif, donc la civilité accorde la phrase une fois pour toutes.
 *   3. Elle pose le mot « Signature » au-dessus du paraphe ; nous ne l'écrivons
 *      pas. C'est une consigne de gabarit, pas une phrase de lettre, et le nom
 *      porté sous le paraphe dit déjà qui signe (décidé le 07/09/2026).
 *
 * Le texte reste en français quelle que soit la langue de l'interface : c'est
 * une pièce d'un dossier français, déposée devant une administration française.
 * Un anglophone qui crée sa micro-entreprise a besoin de comprendre ce qu'il
 * signe, pas d'un document que le guichet refusera.
 */

import {
    adressePostale,
    dateEnToutesLettres,
    identite,
    lieuDeNaissance,
    nettoyer,
} from "./documentText.js";

/** Article visé par la déclaration, et seul texte cité dans le document. */
export const ARTICLE = "l'article A. 123-51 du code de commerce";

/**
 * La peine encourue pour une fausse déclaration, rappelée à l'écran.
 *
 * Article L. 123-5 du code de commerce, alinéa 1, tel que le modèle officiel le
 * cite. Le montant vit ici et nulle part ailleurs.
 */
export const SANCTION = { amende: 4500, moisDEmprisonnement: 6 };

/**
 * Un parent, ou son absence.
 *
 * « Né de père non dénommé » est la formule que porte l'acte de naissance
 * lui-même quand la filiation n'est pas établie : le modèle officiel ne prévoit
 * pas le cas, mais laisser la ligne vide ferait rejeter le dossier.
 */
export function filiation({ prenoms, nom, nonDenomme }, role) {
    if (nonDenomme) return role === "mere" ? "mère non dénommée" : "père non dénommé";
    return identite({ prenoms, nom });
}

/**
 * Ce qui manque encore, et ce qui mérite un mot sans rien bloquer.
 *
 * `manques` porte des clés de champ : l'interface les allume, le harnais les
 * compte. `alertes` porte des motifs, jamais des phrases : les libellés sont
 * chez l'appelant, qui les a dans les deux langues.
 */
export function valider(reponses, aujourdhui) {
    const r = reponses || {};
    const manques = [];
    const invalides = [];
    const alertes = [];

    const requis = [
        // La civilité accorde la phrase entière (« Je soussignée », « née ») :
        // sans réponse, le document parlerait au masculin de quelqu'un qui n'a
        // rien choisi. Elle était lue sans jamais être exigée, ce qu'un bouton
        // présélectionné cachait.
        ["civilite", r.civilite],
        ["prenoms", r.prenoms],
        ["nom", r.nom],
        ["dateDeNaissance", r.dateDeNaissance],
        ["villeDeNaissance", r.villeDeNaissance],
        ["voie", r.voie],
        ["codePostal", r.codePostal],
        ["commune", r.commune],
        ["lieuDeSignature", r.lieuDeSignature],
    ];
    for (const [cle, valeur] of requis) {
        if (!nettoyer(valeur)) manques.push(cle);
    }

    if (!r.pereNonDenomme && !nettoyer(r.perePrenoms)) manques.push("perePrenoms");
    if (!r.pereNonDenomme && !nettoyer(r.pereNom)) manques.push("pereNom");
    if (!r.mereNonDenommee && !nettoyer(r.merePrenoms)) manques.push("merePrenoms");
    if (!r.mereNonDenommee && !nettoyer(r.mereNom)) manques.push("mereNom");

    const cp = nettoyer(r.codePostal);
    if (cp && !/^\d{5}$/.test(cp)) invalides.push({ champ: "codePostal", motif: "format" });

    const naissance = dateEnToutesLettres(r.dateDeNaissance);
    if (nettoyer(r.dateDeNaissance) && !naissance) {
        invalides.push({ champ: "dateDeNaissance", motif: "format" });
    }

    if (naissance) {
        const jour = new Date(`${r.dateDeNaissance}T00:00:00Z`);
        const ref = new Date(aujourdhui);
        const ans = (ref - jour) / (365.2425 * 24 * 3600 * 1000);
        // Une date présente mais aberrante n'est pas une date absente : dire
        // « il manque » devant un champ rempli est le meilleur moyen de perdre
        // le lecteur, qui relit dix fois ce qu'il a déjà écrit.
        if (ans < 0 || ans > 110) {
            invalides.push({ champ: "dateDeNaissance", motif: "ageImpossible" });
        }
        // Un mineur peut déclarer, s'il est émancipé. On le prévient, on ne
        // l'arrête pas : la loi ne l'arrête pas non plus.
        else if (ans < 18) alertes.push("mineur");
    }

    return {
        valide: manques.length === 0 && invalides.length === 0,
        manques: [...new Set(manques)],
        invalides,
        alertes,
    };
}

/**
 * Le document, en blocs, prêt à être posé sur une page ou dans un PDF.
 *
 * Rend `null` tant qu'il manque une réponse : un document à trous ne se
 * télécharge pas, et l'appelant n'a pas à décider ce qu'il fait d'un trou.
 */
export function composerDeclaration(reponses, aujourdhui) {
    const r = reponses || {};
    if (!valider(r, aujourdhui).valide) return null;

    const feminin = r.civilite === "madame";
    const nomComplet = identite({ prenoms: r.prenoms, nom: r.nom });
    const dateDeSignature = nettoyer(r.dateDeSignature) || String(aujourdhui).slice(0, 10);

    return {
        titre: "Déclaration de non-condamnation et de filiation",
        blocs: [
            { type: "paragraphe", texte: `En application des dispositions de ${ARTICLE} :` },
            {
                type: "lignes",
                lignes: [
                    `Je soussign${feminin ? "ée" : "é"} ${nomComplet},`,
                    `n${feminin ? "ée" : "é"} le ${dateEnToutesLettres(r.dateDeNaissance)} à ${lieuDeNaissance(r)},`,
                    `de ${filiation({ prenoms: r.perePrenoms, nom: r.pereNom, nonDenomme: r.pereNonDenomme }, "pere")}`,
                    `et de ${filiation({ prenoms: r.merePrenoms, nom: r.mereNom, nonDenomme: r.mereNonDenommee }, "mere")},`,
                    `demeurant ${adressePostale(r)}`,
                ],
            },
            { type: "paragraphe", texte: "Déclare :" },
            {
                type: "paragraphe",
                texte:
                    `Conformément à ${ARTICLE}, n'avoir été l'objet d'aucune condamnation pénale ` +
                    "ni de sanction civile ou administrative de nature à m'interdire de gérer, " +
                    "administrer, diriger ou contrôler une personne morale, ou d'exercer une " +
                    "activité commerciale.",
            },
            {
                type: "paragraphe",
                texte: `${nettoyer(r.lieuDeSignature)}, le ${dateEnToutesLettres(dateDeSignature)}.`,
            },
            // Sans le mot « Signature » du modèle : une consigne de gabarit ne
            // se lit pas dans une lettre, et le nom porté dessous suffit.
            { type: "signature", nom: nomComplet },
        ],
    };
}
